import { useState } from 'react';
import { Flex, Text, Icon, iconWeight, Calendar, Divider } from '@nula/core-ui';
import { localDateKey } from '@/lib/calendar/date-utils';
import { HomeCalendarWidget } from './home-calendar-widget';
import styles from './merged-calendar-card.module.css';

export interface MergedCalendarCardProps {
  /** Navigate to the full calendar view. */
  onOpenCalendar: () => void;
}

/**
 * Merged calendar card — the day's events (left) and a month date-picker
 * (right) combined into one surface. Replaces the previous two-panel bento
 * (separate "Today" events panel + mini-calendar panel). Selecting a day in the
 * picker focuses the events list on that day.
 */
export function MergedCalendarCard({ onOpenCalendar }: MergedCalendarCardProps) {
  const today = new Date();
  const todayKey = localDateKey(today);
  const [focusedDate, setFocusedDate] = useState<string | null>(null);

  const headingLabel =
    focusedDate && focusedDate !== todayKey
      ? new Intl.DateTimeFormat(navigator.language, { month: 'long', day: 'numeric' }).format(
          new Date(focusedDate + 'T12:00:00'),
        )
      : 'Today';

  return (
    <div className={styles.card}>
      {/* Left: month date-picker */}
      <div className={styles.pickerPane}>
        <Calendar
          variant="picker"
          defaultValue={todayKey}
          onChange={(date) => setFocusedDate(date)}
          fullscreen={false}
        />
      </div>

      {/* Inset vertical divider (core-ui) — doesn't run the full card height. */}
      <Divider orientation="vertical" className={styles.divider} />

      {/* Right: the day's events (scrolls within a capped height) */}
      <Flex vertical className={styles.eventsPane}>
        <Flex align="center" justify="space-between" className={styles.header}>
          <Text className={styles.headerTitle}>{headingLabel}</Text>
          <Flex
            align="center"
            gap="xs"
            onClick={onOpenCalendar}
            className={styles.calendarLink}
          >
            <Text size="xs" className={styles.calendarLinkText}>Open calendar</Text>
            <Icon name="arrow_forward" size={12} weight={iconWeight.light} />
          </Flex>
        </Flex>
        <div className={styles.eventsScroll}>
          <HomeCalendarWidget focusedDate={focusedDate} />
        </div>
      </Flex>
    </div>
  );
}
